import type { PiperVoiceEntry } from './voice-catalogue'

/** The subset of a catalogue entry the worker needs to fetch and verify a voice. */
export type PiperVoiceDescriptor = Pick<
  PiperVoiceEntry,
  'id' | 'modelUrl' | 'configUrl' | 'sha256' | 'sizeBytes' | 'configSha256' | 'revision'
>

export type PiperCommand =
  | { type: 'load'; voice: PiperVoiceDescriptor }
  | { type: 'synthesize'; voiceId: string; text: string }

export type PiperRequest = PiperCommand & { requestId: number }

export type PiperResponse =
  | { type: 'progress'; requestId: number; loaded: number; total: number }
  | { type: 'loaded'; requestId: number }
  | { type: 'audio'; requestId: number; wav: ArrayBuffer; durationSeconds: number }
  | { type: 'error'; requestId: number; message: string }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isVoiceDescriptor(value: unknown): value is PiperVoiceDescriptor {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.modelUrl === 'string' &&
    typeof value.configUrl === 'string' &&
    typeof value.sha256 === 'string' &&
    typeof value.sizeBytes === 'number' &&
    typeof value.configSha256 === 'string' &&
    typeof value.revision === 'string'
  )
}

/** Type guard for messages received by the worker. */
export function isPiperRequest(value: unknown): value is PiperRequest {
  if (!isRecord(value) || typeof value.requestId !== 'number') return false
  if (value.type === 'load') return isVoiceDescriptor(value.voice)
  if (value.type === 'synthesize') {
    return typeof value.voiceId === 'string' && typeof value.text === 'string'
  }
  return false
}

/** Type guard for messages received from the worker. */
export function isPiperResponse(value: unknown): value is PiperResponse {
  if (!isRecord(value) || typeof value.requestId !== 'number') return false
  switch (value.type) {
    case 'progress':
      return typeof value.loaded === 'number' && typeof value.total === 'number'
    case 'loaded':
      return true
    case 'audio':
      return value.wav instanceof ArrayBuffer && typeof value.durationSeconds === 'number'
    case 'error':
      return typeof value.message === 'string'
    default:
      return false
  }
}
